import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { rentRel, rentUrl } from "../data/referrals";
import { HOURS_PER_MONTH, usd } from "../seo/gpuPrices";
import { SPARECORES_PROVIDERS, SpareCoresCredit } from "./SpareCoresCredit";
import { PRICE_PROVIDERS } from "../seo/priceSources";
import { validateHistory, type Gpu, type GpuHistory, type offersFor } from "./llmVram";
import { useDataFile } from "./usePrices";

// Blocs partagés par le calculateur et les pages de prix par GPU : tableau des
// fournisseurs du jour et historique quotidien (graphe, plus bas relevé, moyenne).

type Offer = ReturnType<typeof offersFor>[number];

const providerShort = (name: string) => name.split(" (")[0];

/** Couleur de la courbe d'un fournisseur (src/seo/priceSources.ts). Sans couleur attitrée (source ajoutée
 *  au relevé sans passer par check-vram) : une teinte de secours, toujours la même pour lui, jamais le gris. */
const SPARE = ["#fb923c", "#2dd4bf", "#c084fc", "#facc15", "#38bdf8", "#f472b6"];
const colorOf = (id: string) => PRICE_PROVIDERS[id]?.color ?? SPARE[[...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % SPARE.length];

/** Prix du jour par fournisseur, pour `n` GPU (coût horaire et mensuel). */
/** labelledBy : id du titre qui nomme le tableau pour les lecteurs d'écran. */
export function PriceTable({ offers, n, labelledBy }: { offers: Offer[]; n: number; labelledBy?: string }) {
  const label = n > 1 ? `${n} GPUs` : "1 GPU";
  return (
    <div className="vram-table-wrap">
      <table className="vram-table" aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">Provider</th>
            <th scope="col">Per GPU-hour</th>
            {n > 1 && <th scope="col">{label}, per hour</th>}
            <th scope="col">{label}, per month</th>
            <th scope="col"><span className="sr-only">Rent</span></th>
          </tr>
        </thead>
        <tbody>
          {offers.map((o, i) => (
            <tr key={o.id}>
              <th scope="row" title={o.note}>{providerShort(o.name)}</th>
              <td className={i === 0 ? "vram-table-ok" : undefined}>{usd(o.price)}</td>
              {n > 1 && <td>{usd(n * o.price)}</td>}
              <td>{usd(n * o.price * HOURS_PER_MONTH)}</td>
              <td><a href={rentUrl(o)} target="_blank" rel={rentRel(o)}>Rent<span className="sr-only"> on {providerShort(o.name)} (opens in a new tab)</span> →</a></td>
            </tr>
          ))}
        </tbody>
      </table>
      {offers.some((o) => o.id in SPARECORES_PROVIDERS) && <SpareCoresCredit />}
    </div>
  );
}

type Range = "hourly" | "daily";
type Pt = [number, number]; // [instant en ms, prix]
type Line = { id: string; name: string; color: string; pts: Pt[] };

// Un relevé manque au-delà de ce délai (source en panne) : la courbe s'interrompt.
const GAP_MS: Record<Range, number> = { hourly: 3 * 36e5, daily: 2 * 864e5 };
const UTC_DAY = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
const UTC_TIME = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
const when = (t: number, range: Range) => (range === "daily" ? UTC_DAY.format(t) : `${UTC_DAY.format(t)}, ${UTC_TIME.format(t)} UTC`);

/** Courbes d'une vue : relevés horaires ou dernier prix de chaque jour. */
function linesOf(history: GpuHistory, range: Range): Line[] {
  return Object.entries(history.providers).flatMap(([id, p]) => {
    const raw = range === "hourly" ? p.hourly ?? [] : p.series;
    const pts = raw.map(([d, v]): Pt => [Date.parse(range === "hourly" ? d : `${d}T00:00:00Z`), v]);
    return pts.length ? [{ id, name: providerShort(p.name), color: colorOf(id), pts }] : [];
  });
}

/** Pas « rond » des graduations de prix : 1, 2, 2,5 ou 5 × une puissance de 10. */
function niceStep(raw: number) {
  const p = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((s) => s >= raw)!;
}

/** Prix en vigueur à l'instant t : le dernier relevé, s'il n'est pas trop ancien (courbe en escalier). */
function valueAt(pts: Pt[], t: number, gap: number) {
  let found: Pt | undefined;
  for (const pt of pts) { if (pt[0] > t) break; found = pt; }
  return found && t - found[0] < gap ? found[1] : undefined;
}

/** Relevés horaires en escalier : un prix tient jusqu'au relevé suivant, sans pente inventée.
 *  Prix quotidiens (un relevé par jour) : reliés d'un point à l'autre. */
function linePath(pts: Pt[], x: (t: number) => number, y: (v: number) => number, gap: number, step: boolean) {
  return pts.map(([t, v], i) => {
    const X = x(t).toFixed(1), Y = y(v).toFixed(1);
    return i === 0 || t - pts[i - 1][0] > gap ? `M${X},${Y}` : step ? `H${X}V${Y}` : `L${X},${Y}`;
  }).join("");
}

/** Graduations du temps : les minuits UTC, ou toutes les 6 heures sur moins de deux jours. `max` : autant
 *  qu'en tient la largeur (« Oct 10 » fait ~40 px) ; sur mobile, 7 dates se chevauchaient en « 0Oct9 10ct ». */
function timeTicks(t0: number, t1: number, range: Range, max: number) {
  const day = 864e5;
  const days: number[] = [];
  for (let t = Math.ceil(t0 / day) * day; t <= t1; t += day) days.push(t);
  const thin = (ts: number[]) => { const every = Math.ceil(ts.length / max); return ts.filter((_, i) => i % every === 0); };
  if (range === "hourly" && days.length < 2) {
    const quarter = 6 * 36e5;
    const out: number[] = [];
    for (let t = Math.ceil(t0 / quarter) * quarter; t <= t1; t += quarter) out.push(t);
    return { ticks: thin(out), label: (t: number) => UTC_TIME.format(t) };
  }
  return { ticks: thin(days), label: (t: number) => UTC_DAY.format(t) };
}

function PriceChart({ history }: { history: GpuHistory }) {
  const checks = (r: Range) => new Set(linesOf(history, r).flatMap((l) => l.pts.map(([t]) => t))).size;
  const hasHourly = checks("hourly") >= 2, hasDaily = checks("daily") >= 2;
  const [range, setRange] = useState<Range>(hasHourly ? "hourly" : "daily");
  const [cursor, setCursor] = useState<number | null>(null);
  // Sources choisies par le visiteur (clic sur leur nom dans la légende) : true affichée, false cachée. Sans
  // choix, toutes sauf celles hors d'échelle. Gardé d'une vue (horaire, quotidienne) et d'un GPU à l'autre.
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  // Le dessin suit la largeur réelle : un texte de 11 px reste de 11 px sur mobile.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);

  // Un fournisseur bien plus cher que les autres (Azure ~3x) écraserait toutes les
  // courbes en bas du graphe : caché au départ, un clic sur son nom l'affiche. En partant du plus
  // cher, on écarte chaque prix à plus de 2,5 fois le suivant. Le moins cher ne sert pas de
  // référence : une offre de place de marché à moitié prix (Vast.ai) écartait tous les autres.
  const view: Range = !hasHourly ? "daily" : !hasDaily ? "hourly" : range;
  const all = linesOf(history, view);
  const last = (l: Line) => l.pts.at(-1)![1];
  const byPrice = [...all].sort((a, b) => last(b) - last(a));
  let cut = 0;
  while (cut < byPrice.length - 1 && last(byPrice[cut]) > 2.5 * last(byPrice[cut + 1])) cut++;
  const offScale = byPrice.slice(0, cut);
  const isShown = (l: Line) => picked[l.id] ?? !offScale.includes(l);
  const lines = all.filter(isShown);
  // Axe du temps commun à toutes les sources : il ne bouge pas quand on en cache une.
  const times = [...new Set(all.flatMap((l) => l.pts.map(([t]) => t)))].sort((a, b) => a - b);
  const gap = GAP_MS[view];
  const drawn = times.length >= 2; // sinon un simple message : rien à mesurer
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(200, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [drawn]);

  // Échelle des prix : celle des sources affichées (cacher la plus chère resserre le graphe sur les autres).
  const prices = (lines.length ? lines : all).flatMap((l) => l.pts.map(([, v]) => v));
  const step = niceStep(Math.max(Math.max(...prices) - Math.min(...prices), Math.max(...prices) * 0.04) / 4);
  let lo = Math.floor(Math.min(...prices) / step) * step;
  let hi = Math.ceil(Math.max(...prices) / step) * step;
  if (hi - lo < 2 * step) { lo = Math.max(0, lo - step); hi += step; }
  const ticks = Array.from({ length: Math.round((hi - lo) / step) + 1 }, (_, i) => lo + i * step);
  const tickUsd = (v: number) => `$${v.toFixed(step >= 1 ? 0 : 2)}`;

  const H = W < 480 ? 200 : 240, L = 50, R = 16, T = 14, B = 28;
  const t0 = times[0], t1 = times.at(-1)!;
  const x = (t: number) => L + ((t - t0) / (t1 - t0 || 1)) * (W - L - R);
  const y = (v: number) => T + ((hi - v) / (hi - lo)) * (H - T - B);
  const xt = timeTicks(t0, t1, view, Math.max(2, Math.min(7, Math.floor((W - L - R) / 56))));
  const anchor = (px: number) => (px < L + 24 ? "start" : px > W - R - 24 ? "end" : "middle");

  // Lecture au survol, au doigt (glisser) ou au clavier (flèches) : le relevé le plus proche.
  const at = cursor ?? t1;
  const pick = (clientX: number, el: SVGSVGElement) => {
    const r = el.getBoundingClientRect();
    const t = t0 + (((clientX - r.left) * (W / r.width) - L) / (W - L - R)) * (t1 - t0);
    setCursor(times.reduce((a, b) => (Math.abs(b - t) < Math.abs(a - t) ? b : a)));
  };
  const onKey = (e: ReactKeyboardEvent) => {
    const i = times.indexOf(at);
    const next = e.key === "ArrowLeft" ? times[Math.max(0, i - 1)] : e.key === "ArrowRight" ? times[Math.min(times.length - 1, i + 1)]
      : e.key === "Home" ? t0 : e.key === "End" ? t1 : undefined;
    if (next !== undefined) { e.preventDefault(); setCursor(next); } else if (e.key === "Escape") setCursor(null);
  };
  const switchTo = (r: Range) => { setRange(r); setCursor(null); };

  if (!drawn) {
    return <p className="vram-note">Tracking started on {history.providers[Object.keys(history.providers)[0]].series[0][0]}. The chart fills in with every check.</p>;
  }
  const summary = lines.map((l) => `${l.name} ${usd(l.pts[0][1])} to ${usd(l.pts.at(-1)![1])}`).join(", ") || "no source shown";
  const hidden = all.filter((l) => !isShown(l));
  const toggle = (l: Line) => setPicked((p) => ({ ...p, [l.id]: !isShown(l) }));
  // Sources hors d'échelle que le visiteur n'a pas encore touchées : la légende dit pourquoi elles sont cachées.
  const away = offScale.filter((l) => picked[l.id] === undefined);
  const readout = (t: number) => lines.map((l) => { const v = valueAt(l.pts, t, gap); return `${l.name} ${v === undefined ? "no check" : usd(v)}`; }).join(", ");
  return (
    <figure className="vram-chart">
      <div className="vram-chart-head">
        <p className="vram-chart-when">{cursor === null ? `Latest check: ${when(t1, view)}` : when(cursor, view)}</p>
        <span className="sr-only" aria-live="polite">{cursor === null ? "" : `${when(cursor, view)}: ${readout(cursor)}`}</span>
        {hasHourly && hasDaily && (
          <div className="vram-chart-range" role="group" aria-label="Chart range">
            <button type="button" aria-pressed={view === "hourly"} onClick={() => switchTo("hourly")}>Hourly, 7 days</button>
            <button type="button" aria-pressed={view === "daily"} onClick={() => switchTo("daily")}>Daily</button>
          </div>
        )}
      </div>
      <div className="vram-chart-box" ref={box}>
        <svg
          width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" tabIndex={0}
          aria-label={`Price per GPU-hour, ${view === "hourly" ? "every hourly check" : "last check of each day"} from ${when(t0, view)} to ${when(t1, view)}: ${summary}. Use the arrow keys to read each check.`}
          onPointerMove={(e) => pick(e.clientX, e.currentTarget)} onPointerDown={(e) => pick(e.clientX, e.currentTarget)}
          onPointerLeave={(e) => { if (e.pointerType === "mouse") setCursor(null); }} onKeyDown={onKey} onBlur={() => setCursor(null)}
        >
          {ticks.map((v) => (
            <g key={v}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="vram-chart-grid" />
              <text x={L - 8} y={y(v)} dy="0.32em" textAnchor="end" className="vram-chart-label">{tickUsd(v)}</text>
            </g>
          ))}
          {xt.ticks.map((t) => (
            <text key={t} x={x(t)} y={H - 8} textAnchor={anchor(x(t))} className="vram-chart-label">{xt.label(t)}</text>
          ))}
          {lines.map((l) => <path key={l.id} d={linePath(l.pts, x, y, gap, view === "hourly")} stroke={l.color} className="vram-chart-line" />)}
          {view === "daily" && times.length <= 90 && lines.map((l) => l.pts.map(([t, v]) => (
            <circle key={`${l.id}${t}`} cx={x(t)} cy={y(v)} r={2.5} fill={l.color} />
          )))}
          {cursor !== null && <line x1={x(cursor)} x2={x(cursor)} y1={T} y2={H - B} className="vram-chart-cursor" />}
          {lines.map((l) => {
            const v = valueAt(l.pts, at, gap);
            return v === undefined ? null : <circle key={l.id} cx={x(at)} cy={y(v)} r={4} fill={l.color} className="vram-chart-dot" />;
          })}
          {!lines.length && <text x={(L + W - R) / 2} y={(T + H - B) / 2} textAnchor="middle" className="vram-chart-empty">Pick a source below to draw its prices</text>}
        </svg>
      </div>
      <figcaption className="vram-chart-legend">
        {/* Chaque nom est un bouton : il affiche ou cache la courbe ; le prix lu au curseur reste affiché. */}
        <div className="vram-legend vram-legend-row" role="group" aria-label="Sources drawn on the chart">
          {all.map((l) => {
            const v = valueAt(l.pts, at, gap), on = isShown(l);
            return (
              <button type="button" key={l.id} className="vram-legend-toggle" aria-pressed={on} onClick={() => toggle(l)}
                title={`${on ? "Hide" : "Show"} ${l.name}`}>
                <i style={{ background: on ? l.color : "transparent", borderColor: l.color }} />
                <span className="vram-legend-name">{l.name}</span>
                <b>{v === undefined ? "no check" : usd(v)}</b>
              </button>
            );
          })}
          {hidden.length > 0 && all.length > 1 && (
            <button type="button" className="vram-legend-all" onClick={() => setPicked(Object.fromEntries(all.map((l) => [l.id, true])))}>Show all</button>
          )}
        </div>
        <p className="vram-chart-hint">
          Click a source to show or hide it.
          {away.length > 0 && ` ${away.map((l) => l.name).join(" and ")} ${away.length > 1 ? "start" : "starts"} hidden: far above the others, ${away.length > 1 ? "they" : "it"} would flatten their lines.`}
        </p>
      </figcaption>
    </figure>
  );
}

/** Historique quotidien d'un GPU (fichier exporté chaque jour par le script du VPS). */
export function PriceHistory({ gpu }: { gpu: Gpu }) {
  const history = useDataFile(`/data/history/${gpu.id}.json`, validateHistory);
  if (history.status === "loading") return <p className="vram-note">Loading the daily history…</p>;
  if (!history.data) return <p className="vram-note">The price history is not available right now. Today's prices are up to date.</p>;

  // Plus bas relevé, et moyenne sur 30 jours du prix le plus bas de chaque jour.
  const points = Object.values(history.data.providers).flatMap((p) => p.series.map(([d, v]) => ({ d, v, p: providerShort(p.name) })));
  const lowest = points.reduce((a, b) => (b.v < a.v ? b : a));
  const dailyMin = new Map<string, number>();
  for (const { d, v } of points) dailyMin.set(d, Math.min(dailyMin.get(d) ?? Infinity, v));
  const last30 = [...dailyMin.entries()].sort(([a], [b]) => a.localeCompare(b)).slice(-30).map(([, v]) => v);

  // Un seul jour suivi : ces chiffres ne feraient que répéter le prix du jour.
  return (
    <>
      <PriceChart history={history.data} />
      {dailyMin.size > 1 && (
        <div className="vram-metrics-grid">
          <div className="vram-metric"><span>Lowest recorded</span><strong>{usd(lowest.v)}</strong></div>
          <div className="vram-metric"><span>Where and when</span><strong>{lowest.p}, {lowest.d}</strong></div>
          <div className="vram-metric"><span>Average, last {last30.length} days</span><strong>{usd(last30.reduce((a, b) => a + b, 0) / last30.length)}</strong></div>
        </div>
      )}
    </>
  );
}
